import { create } from "zustand";
import { IEvent } from "../models/events";
import { firebaseApp } from "../config";
import {
  addDoc,
  collection,
  deleteDoc,
  DocumentData,
  documentId,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  QueryDocumentSnapshot,
  orderBy,
  query,
  setDoc,
  startAfter,
  Timestamp,
  where,
} from "firebase/firestore";
import { notifications } from "@mantine/notifications";
import { IPatient } from "../models/patient";
import { startCase } from "../utilities/title";
import { patientKey } from "../utilities/patient-key";

interface IEventStore {
  events: IEvent[];
  history: IEvent[];
  hasMoreHistory: boolean;
  patients: IPatient[];
  patientsFromSearch: IPatient[];
  historyCursor: QueryDocumentSnapshot<DocumentData> | null;
  addEvent: (event: IEvent) => void;
  updateEvent: (event: IEvent) => void;
  getEvents: () => void;
  getEvent: (id: string) => void;
  getHistoryOfEvents: () => void;
  loadMoreHistoryOfEvents: () => Promise<void>;
  deleteEvent: (id: string) => void;
  addPatient: (event: IEvent, patient: IPatient) => Promise<boolean>;
  getPatients: (eventUID: string) => void;
  getPatient: (patientUID: string) => Promise<IPatient | null>;
  updatePatient: (
    eventUID: string,
    patientUID: string,
    patient: IPatient,
  ) => void;
  deletePatient: (eventUID: string, patientUID: string) => void;
  reloadDashboard: () => void;
  sendMessageReminder: (event: IEvent) => void;
  searchPatients: (search: string) => Promise<void>;
  getPatientHistory: (eventIds: string[]) => Promise<IEvent[]>;
}

const HISTORY_PAGE_SIZE = 10;

const mapEventDoc = (doc: QueryDocumentSnapshot<DocumentData>): IEvent => ({
  type: doc.get("type"),
  limit: doc.get("limit"),
  uid: doc.id,
  patientTotal: doc.get("patientTotal") ?? 0,
  date: doc.get("date").toDate().toISOString(),
});

const getPatientDateOfBirth = (
  doc: Pick<QueryDocumentSnapshot<DocumentData>, "get">,
): string => {
  const dob = doc.get("dob") ?? doc.get("dateOfBirth");
  if (!dob) {
    return "";
  }

  return dob.toDate().toISOString().split("T")[0];
};

const mapPatientDoc = (
  doc: Pick<QueryDocumentSnapshot<DocumentData>, "data" | "get" | "id">,
): IPatient => ({
  ...doc.data(),
  firstName: startCase(doc.get("firstName")),
  lastName: startCase(doc.get("lastName")),
  dob: getPatientDateOfBirth(doc),
  phone: doc.get("phone"),
  uid: doc.id,
});

const chunkIds = (ids: string[], size: number): string[][] => {
  const chunks: string[][] = [];

  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size));
  }

  return chunks;
};

// send twilio message when adding
const sendSESMessage = async (eventUID: string, patientUID: string) => {
  const result = await fetch(
    `${import.meta.env.VITE_MAIN_API}/api/v1/send-message/${eventUID}/${patientUID}/`,
  );
  if (!result.ok) {
    notifications.show({
      title: "Error",
      message: "An error occurred while sending SMS",
      color: "red",
    });
  } else {
    notifications.show({
      title: "Success",
      message: "SMS sent successfully",
      color: "green",
    });
  }
};

export const useEventStore = create<IEventStore>((set, get) => ({
  events: [],
  patients: [],
  history: [],
  hasMoreHistory: false,
  historyCursor: null,
  patientsFromSearch: [],
  getEvents: async () => {
    const db = getFirestore(firebaseApp);
    const eventsCollection = collection(db, "events");
    const eventsDocs = await getDocs(
      query(eventsCollection, orderBy("date"), where("date", ">=", new Date())),
    );
    const events = eventsDocs.docs.map((doc) => ({
      type: doc.get("type"),
      limit: doc.get("limit"),
      uid: doc.id,
      patientTotal: doc.get("patientTotal") ?? 0,
      date: doc.get("date").toDate().toISOString(),
    }));
    set((state: IEventStore) => ({ ...state, events }));
  },
  getHistoryOfEvents: async () => {
    const db = getFirestore(firebaseApp);
    const eventsCollection = collection(db, "events");
    const eventsDocs = await getDocs(
      query(
        eventsCollection,
        orderBy("date", "desc"),
        where("date", "<", new Date()),
        limit(HISTORY_PAGE_SIZE),
      ),
    );
    const history = eventsDocs.docs.map(mapEventDoc);
    const historyCursor = eventsDocs.docs.at(-1) ?? null;
    set((state: IEventStore) => ({
      ...state,
      history,
      historyCursor,
      hasMoreHistory: eventsDocs.docs.length === HISTORY_PAGE_SIZE,
    }));
  },
  loadMoreHistoryOfEvents: async () => {
    const { historyCursor, history } = get();
    if (!historyCursor) {
      set((state: IEventStore) => ({ ...state, hasMoreHistory: false }));
      return;
    }

    const db = getFirestore(firebaseApp);
    const eventsCollection = collection(db, "events");
    const eventsDocs = await getDocs(
      query(
        eventsCollection,
        orderBy("date", "desc"),
        where("date", "<", new Date()),
        startAfter(historyCursor),
        limit(HISTORY_PAGE_SIZE),
      ),
    );

    const nextHistory = eventsDocs.docs.map(mapEventDoc);
    const nextCursor = eventsDocs.docs.at(-1) ?? null;
    set((state: IEventStore) => ({
      ...state,
      history: [...history, ...nextHistory],
      historyCursor: nextCursor,
      hasMoreHistory: eventsDocs.docs.length === HISTORY_PAGE_SIZE,
    }));
  },
  addEvent: async (event: IEvent) => {
    const db = getFirestore(firebaseApp);
    const eventCollection = collection(db, "events");
    const newEvent = await addDoc(eventCollection, {
      type: event.type,
      date: Timestamp.fromDate(new Date(event.date)),
      limit: event.limit,
      patientTotal: 0,
    });
    if (newEvent) {
      notifications.show({
        title: "Event Added",
        message: "Event has been added successfully",
        color: "green",
      });
    } else {
      notifications.show({
        title: "Error",
        message: "Failed to add event",
        color: "red",
      });
    }
    get().reloadDashboard();
  },
  updateEvent: async (event: IEvent) => {
    const eventDoc = doc(
      collection(getFirestore(firebaseApp), "events"),
      event.uid,
    );
    await setDoc(
      eventDoc,
      {
        type: event.type,
        date: Timestamp.fromDate(new Date(event.date)),
        limit: event.limit,
      },
      { merge: true },
    );
    notifications.show({
      title: "Event Updated",
      message: "Event has been updated successfully",
      color: "green",
    });
    get().reloadDashboard();
  },
  getEvent: async (id: string) => {
    // not used
    const eventDoc = doc(collection(getFirestore(firebaseApp), "events"), id);
    const event = await getDoc(eventDoc);
    set((state: IEventStore) => ({
      ...state,
      event: {
        type: event.get("type"),
        date: event.get("date").toDate(),
        limit: event.get("limit"),
        uid: event.id,
        patientTotal: event.get("patientTotal") ?? 0,
      },
    }));
  },
  reloadDashboard: () => {
    get().getHistoryOfEvents();
    get().getEvents();
  },
  deleteEvent: async (id: string) => {
    const eventDoc = doc(collection(getFirestore(firebaseApp), "events"), id);
    // check if event doc has patients
    const patientsCollection = collection(eventDoc, "patients");
    const patientsDocs = await getDocs(patientsCollection);
    if (patientsDocs.docs.length > 0) {
      notifications.show({
        title: "Error",
        message: "Cannot delete event with patients",
        color: "red",
      });
      return;
    }
    await deleteDoc(eventDoc);
    notifications.show({
      title: "Event Deleted",
      message: "Event has been deleted successfully",
      color: "green",
    });
    get().reloadDashboard();
  },
  addPatient: async (event: IEvent, patient: IPatient): Promise<boolean> => {
    const eventDoc = doc(
      collection(getFirestore(firebaseApp), "events"),
      event.uid,
    );
    const eventDocData = await getDoc(eventDoc);

    const patientCollection = collection(eventDoc, "patients");

    const patientDoc = doc(
      collection(getFirestore(firebaseApp), "patients"),
      patientKey({
        firstName: patient.firstName,
        lastName: patient.lastName,
        phone: patient.phone,
        dob: patient.dob,
      }),
    );
    const patientDocData = await getDoc(patientDoc);
    if (!patientDocData.exists()) {
      await setDoc(patientDoc, {
        firstName: startCase(patient.firstName),
        lastName: startCase(patient.lastName),
        phone: patient.phone,
        email: patient.email,
        dob: new Date(patient.dob),
        address: patient.address,
        city: patient.city,
        zipCode: patient.zipCode,
        latestEventId: event.uid,
        officeStaffOnly: patient.officeStaffOnly,
        events: [event.uid],
        isNewPatient: patient.isNewPatient,
      });
      const result = await addDoc(patientCollection, {
        ...patient,
        events: [event.uid],
        dob: new Date(patient.dob),
        latestEventId: event.uid,
        firstName: startCase(patient.firstName),
        lastName: startCase(patient.lastName),
      });
      await sendSESMessage(event.uid!, result.id);
    } else {
      if (patientDocData.get("events").includes(event.uid)) {
        notifications.show({
          title: "Error",
          message: "Patient already exists in this event",
          color: "red",
        });
        return false;
      }
      await setDoc(
        patientDoc,
        {
          latestEventId: event.uid,
          events: [...patientDocData.get("events"), event.uid],
        },
        { merge: true },
      );

      const result = await addDoc(patientCollection, {
        ...patient,
        events: [...patientDocData.get("events"), event.uid],
        dob: new Date(patient.dob),
        latestEventId: event.uid,
        firstName: startCase(patient.firstName),
        lastName: startCase(patient.lastName),
      });
      await sendSESMessage(event.uid!, result.id);
    }
    await setDoc(
      eventDoc,
      {
        patientTotal: (eventDocData.get("patientTotal") ?? 0) + 1,
      },
      { merge: true },
    );

    notifications.show({
      title: "Patient Added",
      message: "Patient has been added successfully",
      color: "green",
    });
    get().reloadDashboard();
    get().getPatients(event.uid!);
    return true;
  },
  updatePatient: async (
    eventUID: string,
    patientUID: string,
    patient: IPatient,
  ) => {
    const eventDoc = doc(
      collection(getFirestore(firebaseApp), "events"),
      eventUID,
    );
    const patientDoc = doc(collection(eventDoc, "patients"), patientUID);
    await setDoc(
      patientDoc,
      {
        firstName: startCase(patient.firstName),
        lastName: startCase(patient.lastName),
        phone: patient.phone,
        email: patient.email,
        dob: new Date(patient.dob),
        address: patient.address ?? "",
        city: patient.city ?? "",
        zipCode: patient.zipCode ?? "",
        officeStaffOnly: patient.officeStaffOnly ?? false,
        isNewPatient: patient.isNewPatient ?? "no",
      },
      { merge: true },
    );
    notifications.show({
      title: "Patient Updated",
      message: "Patient has been updated successfully",
      color: "green",
    });
    get().getPatients(eventUID);
  },
  getPatients: async (eventUID: string) => {
    const eventDoc = doc(
      collection(getFirestore(firebaseApp), "events"),
      eventUID,
    );
    // const patientsCollection = getDocs(query(collection(eventDoc, "patients"), orderBy("firstName")));
    const patientsDocs = await getDocs(
      query(collection(eventDoc, "patients"), orderBy("firstName")),
    );
    const patients = patientsDocs.docs.map(mapPatientDoc);
    set((state: IEventStore) => ({ ...state, patients }));
  },
  deletePatient: async (eventUID: string, patientUID: string) => {
    const eventDoc = doc(
      collection(getFirestore(firebaseApp), "events"),
      eventUID,
    );
    const eventDocData = await getDoc(eventDoc);
    const patientDoc = doc(collection(eventDoc, "patients"), patientUID);
    const patient = await getDoc(patientDoc);
    await deleteDoc(patientDoc);
    await setDoc(
      eventDoc,
      {
        patientTotal: (eventDocData.get("patientTotal") ?? 0) - 1,
      },
      { merge: true },
    );
    const patientMainDoc = doc(
      collection(getFirestore(firebaseApp), "patients"),
      patientKey({
        firstName: patient.get("firstName"),
        lastName: patient.get("lastName"),
        phone: patient.get("phone"),
        dob: patient.get("dob").toDate().toISOString().split("T")[0],
      }),
    );
    const patientMainDocData = await getDoc(patientMainDoc);
    await setDoc(
      patientMainDoc,
      {
        events: patientMainDocData
          .get("events")
          .filter((event: string) => event !== eventUID),
      },
      { merge: true },
    );
    notifications.show({
      title: "Patient Deleted",
      message: "Patient has been deleted successfully",
      color: "green",
    });
    get().reloadDashboard();
    get().getPatients(eventUID);
  },
  sendMessageReminder: async (event: IEvent) => {
    const results = await fetch(
      `${import.meta.env.VITE_MAIN_API}/api/v1/reminder/` + event.uid,
    );
    const data = await results.json();
    console.log(data);
  },
  searchPatients: async (search: string) => {
    const searchTerm = search.trim().toLowerCase();
    if (!searchTerm) {
      set((state: IEventStore) => ({ ...state, patientsFromSearch: [] }));
      return;
    }

    const db = getFirestore(firebaseApp);
    const patientsCollection = collection(db, "patients");
    const patientsDocs = await getDocs(query(patientsCollection, orderBy("firstName")));
    const patients = patientsDocs.docs
      .map(mapPatientDoc)
      .filter((patient) => {
        const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
        const email = patient.email?.toLowerCase() ?? "";
        const phone = patient.phone?.replace(/\D/g, "") ?? "";
        const normalizedSearch = searchTerm.replace(/\D/g, "");

        return (
          fullName.includes(searchTerm) ||
          patient.firstName.toLowerCase().includes(searchTerm) ||
          patient.lastName.toLowerCase().includes(searchTerm) ||
          email.includes(searchTerm) ||
          (normalizedSearch.length > 0 && phone.includes(normalizedSearch))
        );
      })
      .slice(0, 25);

    set((state: IEventStore) => ({ ...state, patientsFromSearch: patients }));
  },
  getPatient: async (patientUID: string): Promise<IPatient | null> => {
    const patientDoc = doc(
      collection(getFirestore(firebaseApp), "patients"),
      patientUID,
    );
    const patient = await getDoc(patientDoc);
    if (!patient.exists()) {
      return null;
    }

    return mapPatientDoc(patient);
  },
  getPatientHistory: async (eventIds: string[]): Promise<IEvent[]> => {
    const filteredEventIds = Array.from(new Set(eventIds.filter(Boolean)));
    if (filteredEventIds.length === 0) {
      return [];
    }

    const db = getFirestore(firebaseApp);
    const eventCollection = collection(db, "events");
    const eventChunks = await Promise.all(
      chunkIds(filteredEventIds, 10).map(async (idsChunk) => {
        const eventDocs = await getDocs(
          query(eventCollection, where(documentId(), "in", idsChunk)),
        );

        return eventDocs.docs.map(mapEventDoc);
      }),
    );

    return eventChunks
      .flat()
      .sort(
        (firstEvent, secondEvent) =>
          new Date(secondEvent.date).getTime() - new Date(firstEvent.date).getTime(),
      );
  },
}));
